import * as React from 'react';
import * as classNames from 'classnames';
import isFunction = require('lodash/isFunction');
import {
    assignMicActivityLevel,
    audioEnabled,
    videoEnabled,
    detachTracks,
    enableAudio,
    enableVideo,
} from './service';

import {
    component           as cssComponent,
    componentHidden     as cssComponentHidden,
    remoteMedia         as cssRemoteMedia,
    localMedia          as cssLocalMedia,
    controls            as cssControls,
    controlsBtn         as cssControlsBtn,
    controlsBtnOff      as cssControlsBtnOff,
    controlsBtnDisabled as cssControlsBtnDisabled,
    controlsBtnBig      as cssControlsBtnBig,
    controlsSound       as cssControlsSound,
    controlsVoice       as cssControlsVoice,
    controlsCamera      as cssControlsCamera,
    notification        as cssNotification,
    notificationVoice   as cssNotificationVoice,
    notificationSound   as cssNotificationSound,
    placeholder         as cssPlaceholder,
    placeholderIcon     as cssPlaceholderIcon,
    placeholderWave     as cssPlaceholderWave,
    localPlaceholderIcon as cssLocalPlaceholderIcon,
    localPlaceholderWave as cssLocalPlaceholderWave,
} from './video-pane.module.sass';

export interface IVideoProps {
    audioOutputDeviceId: string;
    audioInputDeviceId: string;
    videoInputDeviceId: string;
    videoQualityId: string;
    visible: boolean;
    token: string;
    room: any;
    onParticipantDisconnected: () => void;
    onReceiveScreen: () => void;
    draggable: boolean;
    isShowingPeerScreen: boolean;
}

export interface IVideoState {
  videoInputEnabled?: boolean;
  audioInputEnabled?: boolean;
  screenSharingOutputEnabled?: boolean;
  audioOutputEnabled?: boolean;
  participantVideoInputEnabled?: boolean;
  localParticipantVideoInputEnabled?: boolean;
  localParticipantAudioInputEnabled?: boolean;
}

// have only one audio context for whole component
// because chrome has limitation of bounding Audio Context
let audioContext: AudioContext;

if ((window as any).AudioContext) {
  audioContext = new AudioContext();
} else if ((window as any).webkitAudioContext) {
  // safari and old versions of Chrome
  audioContext = new (window as any).webkitAudioContext();
}

export class Video extends React.Component<IVideoProps, IVideoState> {
    public state = {
        videoInputEnabled: true,
        audioInputEnabled: true,
        screenSharingOutputEnabled: false,
        audioOutputEnabled: true,
        participantVideoInputEnabled: true,
        localParticipantVideoInputEnabled: true,
        localParticipantAudioInputEnabled: true,
    };

    public componentDidMount(): void {
        this.listen();
    }

    public componentDidUpdate(prevProps: IVideoProps): void {
        const {listen, props: {room}} = this;
        if (prevProps.room !== room) {
            listen();
        }
    }

    public componentWillUnmount(): void {
        const {props: {room}} = this;
        if (!room) return;

        room.removeListener('participantConnected', this.handleParticipantConnected);
        room.removeListener('participantDisconnected', this.handleParticipantDisconnected);
        room.removeListener('disconnected', this.handleDisconnected);
        room.removeListener('trackSubscribed', this.attachAudioOutput);
    }

  public render(): React.ReactElement<any> {
    const {
      videoInputEnabled,
      audioInputEnabled,
      audioOutputEnabled,
      participantVideoInputEnabled,
      localParticipantVideoInputEnabled,
      localParticipantAudioInputEnabled,
    } = this.state;

    const {draggable, isShowingPeerScreen, visible} = this.props;

    return (
      <div className={classNames(cssComponent, {[cssComponentHidden]: !visible})}>
        <div ref={'remote-media'} className={cssRemoteMedia}>
          <div className={classNames(cssPlaceholder, {'is-hidden': participantVideoInputEnabled})}>
            <div className={cssPlaceholderIcon} />
            <div ref={'remote-media-waves'} />
          </div>
        </div>
        <div ref={'local-media'} className={classNames(cssLocalMedia, {
          'is-hidden': isShowingPeerScreen,
        })}>
          <div className={classNames(cssPlaceholder, {'is-hidden': videoInputEnabled})}>
            <div className={cssLocalPlaceholderIcon} />
            <div ref={'local-media-waves'} />
          </div>
        </div>

        <div className={cssNotification}>
          {
            audioInputEnabled ||
            <span className={cssNotificationVoice} />
          }
          {
            audioOutputEnabled ||
            <span className={cssNotificationSound} />
          }
        </div>
        <div className={cssControls}>
          <span className={classNames({
                  [cssControlsBtn]: audioInputEnabled,
                  [cssControlsBtnOff]: !audioInputEnabled,
                  [cssControlsBtnDisabled]: !localParticipantAudioInputEnabled,
                  [cssControlsBtnBig]: !draggable,
                })}
                onClick={this.toggleVoice}>
            <span className={cssControlsVoice} />
          </span>
          <span className={classNames({
                  [cssControlsBtn]: audioOutputEnabled,
                  [cssControlsBtnOff]: !audioOutputEnabled,
                  [cssControlsBtnBig]: !draggable,
                })}
                onClick={this.toggleSound}>
            <span className={cssControlsSound} />
          </span>
          <span className={classNames({
                  [cssControlsBtn]: videoInputEnabled,
                  [cssControlsBtnOff]: !videoInputEnabled,
                  [cssControlsBtnDisabled]: !localParticipantVideoInputEnabled,
                  [cssControlsBtnBig]: !draggable,
                })}
                onClick={this.toggleCamera}>
            <span className={cssControlsCamera} />
          </span>
        </div>
      </div>
    );
  }

    private listen = () => {
        const {props: {room}} = this;
        if (!room) return;

        room.localParticipant.tracks.forEach(trackPub => {
          const {track} = trackPub;
          (this.refs['local-media'] as HTMLElement).appendChild(track.attach());
        });

        room.localParticipant.audioTracks.forEach(trackPub => {
          const {track} = trackPub;
          assignMicActivityLevel(
            audioContext,
            track,
            this.refs['local-media-waves'] as HTMLElement,
            cssLocalPlaceholderWave,
          );
        });

        this.setState({
          audioInputEnabled: audioEnabled(room.localParticipant),
          videoInputEnabled: videoEnabled(room.localParticipant),
          localParticipantAudioInputEnabled: audioEnabled(room.localParticipant),
          localParticipantVideoInputEnabled: videoEnabled(room.localParticipant),
        });

        room.participants.forEach(this.handleParticipantConnected);
        room.on('participantConnected', this.handleParticipantConnected);
        room.on('participantDisconnected', this.handleParticipantDisconnected);
        room.on('disconnected', this.handleDisconnected);
        room.on('trackSubscribed', this.attachAudioOutput);
    };

  private attachAudioOutput = (track) => {
    if (track.kind !== 'audio') {
      return;
    }

    const audioElement = track.attach();

    if (isFunction(audioElement.setSinkId)) {
      audioElement
        .setSinkId(this.props.audioOutputDeviceId)
        .then(() => (this.refs['local-media'] as HTMLElement).appendChild(audioElement));
    }
  }

    private handleParticipantConnected = participant => {
        const setParticipantVideoInputStatus = () => {
          this.setState({participantVideoInputEnabled: videoEnabled(participant)});
        };

        const handleTrackAdded = track => {
          if (track.kind === 'audio') {
            enableAudio(participant, this.state.audioOutputEnabled);
            assignMicActivityLevel(
              audioContext,
              track,
              this.refs['remote-media-waves'] as HTMLElement,
              cssPlaceholderWave,
            );
          }

          const media = this.refs['remote-media'] as HTMLElement;
          if (!media) return;

          const {props: {isShowingPeerScreen, onReceiveScreen}} = this;

          // Don't show the screen track unless we're in screen sharing mode.
          if (track.kind === 'audio' || (!isShowingPeerScreen && track.name !== 'screen')) {
            media.appendChild(track.attach());
          } else if (isShowingPeerScreen && track.name === 'screen') {
            media.appendChild(track.attach());
            onReceiveScreen();
          }
        };

        const handlePublicationAdded = publication => {
          publication.on('subscribed', handleTrackAdded);
        }

        const handleTrackUnsubscribed = track => {
          track.detach().forEach((el: HTMLElement) => el.remove());
        }

        participant.tracks.forEach(handlePublicationAdded);
        participant.on('trackPublished', handlePublicationAdded);
        participant.on('trackUnsubscribed', handleTrackUnsubscribed);
        participant.on('trackStarted', setParticipantVideoInputStatus);
        participant.on('trackEnabled', setParticipantVideoInputStatus);
        participant.on('trackDisabled', setParticipantVideoInputStatus);
    };

  private handleParticipantDisconnected = (participant) => {
    this.props.onParticipantDisconnected();
    this.setState({participantVideoInputEnabled: true});
  };

  private handleDisconnected = (room): void => {
    detachTracks(room.localParticipant);
    room.participants.forEach(this.handleParticipantDisconnected);
  };

  private toggleVoice = (): void => {
    if (!this.state.localParticipantAudioInputEnabled) {
      return;
    }
    this.setState({audioInputEnabled: !this.state.audioInputEnabled}, () => {
      const {props: {room}} = this;
      if (room) {
        enableAudio(room.localParticipant, this.state.audioInputEnabled);
      }
    });
  };

  private toggleSound = (): void => {
    this.setState({audioOutputEnabled: !this.state.audioOutputEnabled}, () => {
      const {props: {room}} = this;
      if (room) {
        room.participants.forEach(participant => {
          enableAudio(participant, this.state.audioOutputEnabled);
        });
      }
    });
  };

  private toggleCamera = (): void => {
    if (!this.state.localParticipantVideoInputEnabled) {
      return;
    }
    this.setState({videoInputEnabled: !this.state.videoInputEnabled}, () => {
      const {props: {room}} = this;
      if (room) {
        enableVideo(room.localParticipant, this.state.videoInputEnabled);
      }
    });
  };
}
